import { PERIODS, REF_PERIOD, USAGES } from './domain';
import type { PeriodKey, RatioSet, RatioTable, UsageKey } from './types';

/**
 * Coefficients de performance (%) : part de consommation d'une période de construction
 * par rapport à la période de référence (la plus ancienne), qui vaut toujours 100 %.
 * HYPOTHÈSE DE DÉMONSTRATION : le coefficient s'applique aux trois postes (chauffage, ECS, froid).
 */
export type PerfTable = Record<UsageKey, Record<PeriodKey, number>>;

/** Un bâtiment plus récent consomme moins que la référence : le coefficient reste sous 100 %. */
export const PERF_MIN = 1;
export const PERF_MAX = 100;
export const isValidPerf = (v: number) => Number.isFinite(v) && v >= PERF_MIN && v <= PERF_MAX;

const round1 = (v: number) => Math.round(v * 10) / 10;
const scale = (r: RatioSet, k: number): RatioSet => ({
  chauffage: round1(r.chauffage * k), ecs: round1(r.ecs * k), froid: round1(r.froid * k),
});

/** Coefficient lu dans la table de ratios : chauffage de la période / chauffage de la référence. */
export function perfOf(t: RatioTable, u: UsageKey, p: PeriodKey): number {
  const ref = t[u][REF_PERIOD].chauffage;
  if (p === REF_PERIOD || ref <= 0) return 100;
  return Math.round((t[u][p].chauffage / ref) * 1000) / 10;
}

export const perfTableOf = (t: RatioTable): PerfTable =>
  Object.fromEntries(USAGES.map((u) => [
    u.key,
    Object.fromEntries(PERIODS.map((p) => [p.key, perfOf(t, u.key, p.key)])),
  ])) as PerfTable;

/** Applique un coefficient à une période : ses ratios repartent de la période de référence. */
export function setPerf(t: RatioTable, u: UsageKey, p: PeriodKey, percent: number): RatioTable {
  if (p === REF_PERIOD) return t;
  return { ...t, [u]: { ...t[u], [p]: scale(t[u][REF_PERIOD], percent / 100) } };
}

/** Recale toute la table sur ses coefficients de chauffage : les trois postes suivent le même %. */
export function alignToPerf(t: RatioTable): RatioTable {
  return Object.fromEntries(USAGES.map((u) => [
    u.key,
    Object.fromEntries(PERIODS.map((p) => [
      p.key,
      p.key === REF_PERIOD ? t[u.key][REF_PERIOD] : scale(t[u.key][REF_PERIOD], perfOf(t, u.key, p.key) / 100),
    ])),
  ])) as RatioTable;
}
